import cn from 'classnames';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import isSwitchingToCMYK from 'editor/src/store/app/selector/isSwitchingToCMYK';
import getDesignColorSpace from 'editor/src/store/design/selector/getDesignColorSpace';
import { ColorSpace } from 'editor/src/store/design/types';
import { useSelector } from 'editor/src/store/hooks';
import getCurrentSpreadColorPickerConfig from 'editor/src/store/hostSettings/selector/getCurrentSpreadColorPickerConfig';
import shouldDigitizeCurrentSpread from 'editor/src/store/utils/shouldDigitizeCurrentSpread';

import CMYK from 'editor/src/util/color/cmykUtils';
import { EMPTY_RGBA_STRING } from 'editor/src/util/color/rgbaUtils';

import { parseColorToRGBA } from '../../util/color/colorUtils';
import SplitterHorizontal from '../TopMenuDesktop/buttons/SplitterHorizontal';

import ColorControllers from './ColorControllers';
import colors from './colors';
import Palette from './Palette';
import ResetButton from './ResetButton';

import styles from './index.module.scss';

interface Props {
  color: string | undefined;
  onColorChange: (color: string | undefined, opacity?: number) => void;
  onColorChanging?: (color: string | undefined, opacity?: number) => void;
  allowNoColor?: boolean;
  allowNoColorLabel?: string;
  allowOpacity?: boolean;
  colors?: string[];
  paletteColorsInRowCount?: number;
  hidePickFromPaletteNote?: true;
  suggestedColors?: string[];
  ignoreCMYK?: boolean;
}

function ColorPicker({
  color,
  onColorChanging: onColorChangingProp,
  onColorChange: onColorChangeProp,
  allowNoColor,
  allowOpacity,
  colors: availableColors,
  suggestedColors,
  allowNoColorLabel,
  paletteColorsInRowCount = 8,
  hidePickFromPaletteNote,
  ignoreCMYK,
}: Props) {
  const { t } = useTranslation();
  const colorPickerConfig = useSelector(getCurrentSpreadColorPickerConfig);
  const cmykOnly = useSelector((state) => !!state.hostSettings.cmykOnly);
  // Thread colours are matched by their exact hex, so an embroidered spread picks in RGB, lock or not; under the
  // lock a hex-only caller (SVG recolour) does too.
  const digitized = useSelector(shouldDigitizeCurrentSpread);
  const followsDesign = !digitized && (!cmykOnly || !ignoreCMYK);
  const colorSpace = useSelector((state) => (followsDesign ? getDesignColorSpace(state) : ColorSpace.sRGB));
  const profileLoaded = useSelector((state) => !!state.app.colorProfileLoaded);
  // under the CMYK lock a pick made before the print profile loads would store the rough fallback colour
  const waitingForProfile = useSelector(
    (state) => colorSpace === ColorSpace.CMYK && cmykOnly && isSwitchingToCMYK(state),
  );
  // The waiting style only stops the mouse; a value typed into HEX or CMYK must not land either.
  const onColorChange = useCallback(
    (...args: Parameters<Props['onColorChange']>) => {
      if (!waitingForProfile) {
        onColorChangeProp(...args);
      }
    },
    [waitingForProfile, onColorChangeProp],
  );
  const onColorChanging = useMemo(
    () =>
      onColorChangingProp &&
      ((...args: Parameters<Props['onColorChange']>) => {
        if (!waitingForProfile) {
          onColorChangingProp(...args);
        }
      }),
    [waitingForProfile, onColorChangingProp],
  );

  const onResetColor = useCallback(() => {
    if (allowNoColor) {
      onColorChange(undefined);
    }
  }, [allowNoColor, onColorChange]);

  const onPaletteColorSelect = useCallback(
    (paletteColor: string) => {
      if (paletteColor === EMPTY_RGBA_STRING) {
        onColorChange(undefined);
        return;
      }
      if (colorSpace === ColorSpace.CMYK) {
        const rgba = parseColorToRGBA(paletteColor);
        onColorChange(CMYK.fromRGB(rgba).toString());
        return;
      }
      onColorChange(paletteColor);
    },
    [colorSpace, onColorChange],
  );

  const noColor = !color || color === EMPTY_RGBA_STRING;
  const paletteColors = availableColors || colors;
  // profileLoaded: the tiles' inks are exact only once the profile has loaded
  const selectedColor = useMemo(() => {
    if (noColor) {
      return undefined;
    }
    // Under the lock a palette pick is stored as ink, whose soft-proof hex is not the tile's own.
    const ink = cmykOnly && colorSpace === ColorSpace.CMYK ? CMYK.fromString(color) : undefined;
    const tile =
      ink &&
      paletteColors.find((paletteColor) => {
        const rgba = parseColorToRGBA(paletteColor);
        return rgba.a === 1 && CMYK.fromRGB(rgba).isEqual(ink);
      });
    return tile ?? parseColorToRGBA(color).toHex();
  }, [noColor, color, cmykOnly, colorSpace, paletteColors, profileLoaded]);

  return (
    <div
      className={cn('cy-color-picker', { [styles.waiting]: waitingForProfile })}
      aria-busy={waitingForProfile || undefined}
    >
      {allowNoColor && (
        <ResetButton
          className="mb-2 mt-2"
          label={allowNoColorLabel || t('No color')}
          active={noColor}
          onReset={onResetColor}
        />
      )}
      <div className={styles.palette}>
        {!hidePickFromPaletteNote && <span className={styles.label}>{t('editor-pick-from-palette')}</span>}
        <Palette
          colors={paletteColors}
          selectedColor={selectedColor}
          onColorSelect={onPaletteColorSelect}
          suggestedColors={suggestedColors}
          colorsInRowCount={paletteColorsInRowCount}
          colorsInfo={colorPickerConfig?.colorsInfo}
          colorSpace={colorSpace}
        />
      </div>
      {!availableColors?.length && (
        <>
          <SplitterHorizontal />
          <ColorControllers
            color={color}
            colorSpace={colorSpace}
            hideRGB={(allowNoColor && noColor) || undefined}
            onColorChange={onColorChange}
            onColorChanging={onColorChanging}
            className={styles.picker}
            label={t('editor-create-custom-color')}
            allowOpacity={!!allowOpacity}
            ignoreCMYK={ignoreCMYK || digitized}
          />
        </>
      )}
    </div>
  );
}

export default React.memo(ColorPicker);
